import { useEffect, useMemo, useState } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { LoginMode, LoginType } from '@/hooks/login/constant'
import { reqApiAgentIndexGetvalidatecode, reqApiAgentIndexLogin, reqApiAgentCenterSendValidCode, useSWR, reqApiAgentIndexPhoneLogin } from '@/apis/ok'
import { getDevice, md5 } from '@/utils'
import { setLoading, clearLoading, toast } from '@/ui/useToastStore'
import { setStorageState, useStorageStore } from '@/website/useStorageStore'
import { setUserState } from '@/store/user/useUserStore'
import { useCommon } from '@/core/useCommon'
import { ROUTES } from '@/env/routes'
import { useMemoizedFn } from 'ahooks'
import { API_CONST, setHeaders } from '@/apis'
import { useLocation } from 'react-router'
import { useFormTrigger } from '../common/useFormTrigger'
import { isStrongPassword } from '../update-password/passwordRules'

// 登入頁 hook
export const useOnLogin = () => {
  const { t } = useCommon()
  const { replace } = useCommon()
  const loginStorage = useStorageStore((state) => state.login)
  const [activeTab, setActiveTab] = useState<LoginMode>(LoginMode.ACCOUNT)
  const [remember, setRemember] = useState<boolean>(!!loginStorage?.account)
  const location = useLocation()

  // 驗證 schema
  const schema = z.object({
    [LoginType.ACCOUNT]: activeTab === LoginMode.ACCOUNT ? z.string().min(1, t('old:plzInputAccount')) : z.string().optional(),
    [LoginType.PASSWORD]: activeTab === LoginMode.ACCOUNT ? z.string().min(1, t('old:plsEnterpasword')) : z.string().optional(),
    [LoginType.GOOGLE_VALID_CODE]: z.string().optional(),
    [LoginType.TELEPHONE]:
      activeTab === LoginMode.MOBILE
        ? z.string().regex(/^09\d{9}$/, {
            message: ' ',
          })
        : z.string().optional(),
    [LoginType.VALID_CODE]: z.string().optional(),
  })

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
  })
  useFormTrigger({ form })

  /** 自訂 onSubmit */
  const onSubmit = form.handleSubmit(async (data) => {
    try {
      setLoading()

      const data = form.getValues()

      if (activeTab === LoginMode.MOBILE && !data.validCode) {
        toast(t('B747'))
        return
      }

      const validCodeIdentity = getValidateCodeQuery?.data?.t?.identity || ''
      const loginSrc = String(getDevice() === 'PC' ? 0 : 1)

      // 手機登入參數
      const paramsMobile = {
        ...data,
        validCodeIdentity,
        useCodeLogin: true,
      } as OK_API_AGENT_INDEX_PHONELOGIN.REQ

      // 帳號登入參數
      const paramsAccount = {
        ...data,
        loginSrc,
        validCodeIdentity,
        password: md5(data.password ?? ''),
      } as OK_API_AGENT_INDEX_LOGIN.REQ

      // 刪除後 api 才會回傳 "请输入Google验证码"
      if (!paramsAccount.googleCode) delete paramsAccount.googleCode

      // 呼叫 API
      const res = activeTab === LoginMode.MOBILE ? await reqApiAgentIndexPhoneLogin.call({ toast: true }, paramsMobile) : await reqApiAgentIndexLogin.call({ toast: true }, paramsAccount)

      // 成功登入
      const token = res?.t?.token
      if (!!token) {
        setHeaders({ [API_CONST.X_SESSION_TOKEN]: token })
        setUserState({
          token,
          userInfo: res.t,
          // 帳號登入時檢查密碼強度，不足則強制修改密碼
          needUpdatePwd: activeTab === LoginMode.ACCOUNT && !isStrongPassword(data.password ?? ''),
        })

        // 僅帳號登入才記住密碼
        if (activeTab === LoginMode.ACCOUNT) {
          setStorageState({
            login: remember
              ? {
                  account: data.account ?? '',
                  password: data.password ?? '',
                }
              : {
                  account: '',
                  password: '',
                },
          })
        }

        if (location?.pathname === ROUTES.LOGIN()) {
          // 如果在登入頁就導回首頁
          replace(ROUTES.HOME())
        }

        return
      }

      // 回調
      // onSuccess?.(res)
    } catch (error) {
      // 回調
      // onFail?.(error)
    } finally {
      clearLoading()
    }
  })

  // 表單欄位
  const fields = useMemo(() => {
    return [
      {
        key: LoginType.ACCOUNT,
        placeholder: t('old:account'),
        type: 'text',
        hidden: activeTab !== LoginMode.ACCOUNT,
      },
      {
        key: LoginType.PASSWORD,
        placeholder: t('old:password'),
        type: 'password',
        hidden: activeTab !== LoginMode.ACCOUNT,
      },
      {
        key: LoginType.GOOGLE_VALID_CODE,
        placeholder: t('old:googlevalidCode'),
        type: 'text',
        hidden: activeTab !== LoginMode.ACCOUNT,
      },
      {
        key: LoginType.TELEPHONE,
        placeholder: '',
        type: 'text',
        hidden: activeTab !== LoginMode.MOBILE,
      },
      {
        key: LoginType.VALID_CODE,
        placeholder: t('old:phoneVerificationCode'),
        type: 'text',
        hidden: activeTab !== LoginMode.MOBILE,
      },
    ]
  }, [t, activeTab, form])

  const getValidateCodeQuery = useSWR<OK_API_AGENT_INDEX_GETVALIDATECODE.RES>(`/index/getvalidatecode`, reqApiAgentIndexGetvalidatecode, {
    params: () => {
      return {
        lastIdentity: getValidateCodeQuery?.data?.t?.identity || '',
      }
    },
    retry: false,
  })

  // 發送驗證碼
  const sendValidCode = useMemoizedFn(async () => {
    const telephone = form.getValues()[LoginType.TELEPHONE] || ''

    try {
      const res = await reqApiAgentCenterSendValidCode.call(
        { toast: true },
        {
          telephone,
        },
      )
      if (res.success) {
        toast({ message: res.msg || '', type: 'success' })
      }
    } catch (error) {
      //
    }
  })

  useEffect(() => {
    // 換 tab 時要把驗證清掉
    form?.reset()

    if (activeTab === LoginMode.ACCOUNT) {
      // 自動填入
      form.setValue(LoginType.ACCOUNT, loginStorage?.account)
      form.setValue(LoginType.PASSWORD, loginStorage?.password)
    }
  }, [activeTab])

  return {
    onSubmit, // 提交事件
    fields, // 表單欄位
    form, // form 物件
    activeTab, // 當前 tab
    setActiveTab, // 設定 tab
    remember, // 記住密碼
    setRemember, // 設定記住密碼
    sendValidCode, // 發送驗證碼
  }
}
